import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Store from '../../../../redux/Store';
import moment from 'moment';
import { AbortController } from '@azure/abort-controller';
import { IconButton, Tooltip, Zoom } from '@mui/material';
import { GetApp as DownloadIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
  withRouter,
  getQueryParam,
  setQueryParam,
} from '../../../../common/utils/NavigationWrapper';
import { showSnackbar } from '../../../../common/state/snackbar/Snackbar.actions';
import Constants from '../../../../common/constants/Constants';
import { getLogList, createLog, transferAttachementToCloud } from './LogBook.actions';
import EditLog from './EditLog';
import LogBookView from './LogBookView';
import { GetFileFromBlob, FormatUploadFileName } from './CloudFileTransfer';
import { logToAppInsights } from '../../../../common/utils/AppInsights';

const LogBook = (props) => {
  const [logBook, setLogbook] = useState({ logbookData: [], metadata: {} });
  const [addDialog, setAddDialog] = useState({ show: false, disableAction: true });

  const [filterDate, setFilterDate] = useState({
    dateFrom: moment.utc().startOf('day'),
    dateTo: moment.utc().startOf('day').add(1, 'days'),
    maxDate: moment.utc().startOf('day').add(1, 'days'),
    dateTimeErrorMessage: null,
  });
  const [isTransferingData, setIsTransferingData] = useState(true);

  const [logBookLog, setLogbookLog] = useState({
    LogBookId: '',
    User: '',
    SerialNumber: props.params.id,
    Action: '',
    FileName: '',
    CloudFile: '',
    UploadCompleted: 0,
    FileInput: {},
  });

  const [abortController, setAbortController] = useState(new AbortController());

  const query = useMemo(
    () => ({
      SelectedInstrument: props.params.id,
      ReportName: 'instrumentLogBook',
      StartDate: filterDate.dateFrom.toISOString(),
      EndDate: filterDate.dateTo.toISOString(),
    }),
    [filterDate, props]
  );

  const columnsConfig = [
    {
      name: 'Id',
      key: 'LogBookId',
      type: 'string',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Time',
      key: 'LogBookTimeStamp',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Action',
      key: 'Action',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'User',
      key: 'User',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'FileName',
      key: 'FileName',
      type: 'string',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Attachement',
      key: 'Actions',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        return el.HasAttachement === true ? (
          <div>
            <Tooltip TransitionComponent={Zoom} title="Download">
              <IconButton
                key={1}
                onClick={() => {
                  return _onClickDownload(el.LogBookId, el.FileName);
                }}
                data-qa="qa-download"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
        ) : null;
      },
      visible: true,
      filterable: false,
      sortable: false,
    },
  ];

  const _openAddLog = useCallback(() => {
    setAddDialog({
      show: true,
      title: 'Add new log',
      actionLabel: 'Submit',
      disableAction: true,
    });

    setLogbookLog({ ...logBookLog, LogBookTimeStamp: moment.utc() });
  }, [logBookLog]);

  const _handleAdd = useCallback(async () => {
    if (isTransferingData) {
      Store.dispatch(
        showSnackbar({
          type: Constants.SNACKBAR_MESSAGE_TYPES.WARNING,
          message: 'A transfer is in progress. Please wait until it finishes.',
        })
      );

      return;
    }
    setIsTransferingData(true);
    let newGuid = uuidv4();

    let newLogBookLog = {
      ...logBookLog,
      LogBookId: newGuid,
      HasAttachement: false,
    };

    try {
      await transferAttachementToCloud(
        newLogBookLog,
        _uploadProgressHandler,
        abortController.signal
      );

      Store.dispatch(
        showSnackbar({
          type: Constants.SNACKBAR_MESSAGE_TYPES.SUCCESS,
          message: 'Successfully added the log.',
        })
      );

      const logs = await _handleRequest(query);
      setLogbook(logs);
    } catch (err) {
      if (err.message && err.message.indexOf('aborted') === -1) {
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
            message: err.message,
          })
        );
      }
    } finally {
      setAddDialog({
        ...addDialog,
        show: false,
      });
      setLogbookLog({
        ...logBookLog,
        LogBookId: '',
        Action: '',
        FileName: '',
        CloudFile: '',
        UploadCompleted: 0,
        FileInput: {},
      });
      setIsTransferingData(false);
    }
  }, [logBookLog, addDialog, setAddDialog]);

  const _uploadProgressHandler = useCallback(
    (value) => {
      setLogbookLog({ ...logBookLog, UploadCompleted: value });
    },
    [logBookLog]
  );

  const _handleClose = () => {
    try {
      abortController.abort();
    } catch {}
    setAbortController(new AbortController());
    setAddDialog({ ...addDialog, show: false });
    setLogbookLog({
      LogBookId: '',
      User: '',
      SerialNumber: props.params.id,
      Action: '',
      FileName: '',
      CloudFile: '',
      UploadCompleted: 0,
      FileInput: {},
    });
  };

  const _handleChangeFor = (propertyName, value) => {
    if (propertyName === 'LogBookTimeStamp') {
      const inputValueDate = new Date(value);
      const currentDate = moment();
      if (inputValueDate > currentDate) {
        value = currentDate;
      }
    }
    setLogbookLog({ ...logBookLog, [propertyName]: value });
  };

  const _handleFileBrowse = () => {
    setLogbookLog({ ...logBookLog, FileName: logBookLog.FileInput.files[0].name });
  };

  const _determinePropertyValue = useCallback(
    (propertyValue, propertyNameInState) => {
      return propertyValue != null ? propertyValue : filterDate[propertyNameInState];
    },
    [filterDate]
  );

  const _checkDateTimeCorrectness = (dateFrom, dateTo) => {
    let dateTimeErrorMessage;
    if (moment(dateFrom).isAfter(dateTo)) {
      dateTimeErrorMessage = Constants.DATEFROM_AFTER_DATETO_ERROR_MESSAGE;
    }

    if (moment(dateTo).isBefore(dateFrom)) {
      dateTimeErrorMessage = Constants.DATETO_BEFORE_DATEFROM_ERROR_MESSAGE;
    }

    return dateTimeErrorMessage;
  };

  const _onChangeDateTime = (key, date) => {
    let newDateTimeErrorMessage = null,
      newDateFrom = null,
      newDateTo = null;

    switch (key) {
      case 'dateFromFilter': {
        newDateFrom = moment(date).startOf('day');
        newDateTo = filterDate.dateTo;
        break;
      }
      case 'timeFromFilter': {
        newDateFrom = date;
        newDateTo = filterDate.dateTo;
        break;
      }
      case 'dateToFilter': {
        newDateTo = moment(date).startOf('day');
        newDateFrom = filterDate.dateFrom;
        break;
      }
      case 'timeToFilter': {
        newDateTo = date;
        newDateFrom = filterDate.dateFrom;
        break;
      }
      default:
        break;
    }

    newDateTimeErrorMessage = _checkDateTimeCorrectness(newDateFrom, newDateTo);

    setFilterDate({
      ...filterDate,
      dateFrom: newDateFrom,
      dateTo: newDateTo,
      dateTimeErrorMessage: newDateTimeErrorMessage,
    });
  };

  const _onApplyFilters = useCallback(async () => {
    setIsTransferingData(true);
    const logs = await _handleRequest(query);

    setLogbook(logs);
    setIsTransferingData(false);
  }, [filterDate, props, query]);

  const _handleRequest = async (query) => {
    let queryResults = {
      logbookData: [],
      metadata: {},
    };
    try {
      const response = await getLogList(query);
      if (response && response.body && Array.isArray(response.body)) {
        queryResults.metadata = response.body.filter(
          (el) => el.Type == 'Filter'
        )[0].Configuration.metadata;

        let tableResults = response.body.filter((el) => el.Type == 'Table');
        queryResults.logbookData =
          tableResults.length != 0 && tableResults[0].Data ? tableResults[0].Data : [];
        return queryResults;
      } else {
        throw new Error('Invalid response format or missing data');
      }
    } catch (err) {
      let reqUrl = err.hasOwnProperty('response') ? err.response.req.url : Constants.REPORTS_URL;
      logToAppInsights(
        'logbook',
        `${err.message}, ${reqUrl}, http response status: ${err.status}`,
        'error'
      );
      queryResults.metadata.HasData = false;

      return queryResults;
    }
  };

  const _onClickDownload = async (logBookId, fileName) => {
    let CloudFile = `${logBookId}/${FormatUploadFileName(fileName)}`;

    try {
      const file = await GetFileFromBlob(props.params.id, CloudFile);
      let downloadWindow = window.open(file);
      downloadWindow.focus();
    } catch (err) {
      Store.dispatch(
        showSnackbar({
          type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
          message: 'Failed to download the selected file.',
        })
      );
    }
  };

  const _textInputIsValid = (inputText) => {
    const trimmedText = typeof inputText === 'string' ? inputText.trim() : '';

    if (trimmedText.length < 3) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    const dateFrom = getQueryParam('dateFrom', 'customDateTimeType', props.querySearchParams);
    const dateTo = getQueryParam('dateTo', 'customDateTimeType', props.querySearchParams);

    const newDateFrom = _determinePropertyValue(dateFrom, 'dateFrom');
    const newDateTo = _determinePropertyValue(dateTo, 'dateTo');
    const newDateTimeErrorMessage = _checkDateTimeCorrectness(newDateFrom, newDateTo);

    const initialFilterDateState = {
      ...filterDate,
      dateFrom: newDateFrom,
      dateTo: newDateTo,
      dateTimeErrorMessage: newDateTimeErrorMessage,
    };

    setFilterDate(initialFilterDateState);

    (async () => {
      const queryFilters = {
        SelectedInstrument: props.params.id,
        ReportName: 'instrumentLogBook',
        StartDate: initialFilterDateState.dateFrom.toISOString(),
        EndDate: initialFilterDateState.dateTo.toISOString(),
      };
      const logs = await _handleRequest(queryFilters);
      setLogbook(logs);
      setIsTransferingData(false);
    })();
  }, [props.instrument.SerialNumber]);

  useEffect(() => {
    let newQueryParams = [
      {
        value: filterDate.dateFrom,
        key: 'dateFrom',
        type: 'customDateTimeType',
      },
      {
        value: filterDate.dateTo,
        key: 'dateTo',
        type: 'customDateTimeType',
      },
    ];

    setQueryParam(newQueryParams, props.querySearchParams, props.setQuerySearchParams);
  }, [filterDate]);

  useEffect(() => {
    if (_textInputIsValid(logBookLog.Action)) {
      setAddDialog({ ...addDialog, disableAction: false });
    } else {
      setAddDialog({ ...addDialog, disableAction: true });
    }
  }, [logBookLog]);

  return (
    <div>
      <LogBookView
        dateFrom={filterDate.dateFrom}
        dateTo={filterDate.dateTo}
        maxDate={filterDate.maxDate}
        dateTimeErrorMessage={filterDate.dateTimeErrorMessage}
        onChangeDateTime={_onChangeDateTime}
        onApplyFilters={_onApplyFilters}
        IsFetching={isTransferingData}
        columns={columnsConfig}
        data={logBook.logbookData}
        metadata={logBook.metadata}
        instrumentInfo={logBookLog.SerialNumber}
        openAddLog={_openAddLog}
      />
      <EditLog
        title={addDialog.title}
        onClickCancel={_handleClose}
        onClickMainAction={_handleAdd}
        mainActionLabel={addDialog.actionLabel}
        disableMainAction={addDialog.disableAction}
        open={addDialog.show}
        logBooklog={logBookLog}
        handleChangeFor={_handleChangeFor}
        onClickFileBrowse={_handleFileBrowse}
      />
    </div>
  );
};

export default withRouter(LogBook);
